// Shared paste + CSV + "Score my UTMs" block. Lifted out of the public
// `UtmHealthCheckPage` so the in-app `/app/utm-health` page can reuse it
// verbatim as its "Validate more URLs" panel (M6.4).
//
// One component, two variants:
//  - variant="public": marketing surface — keeps `.ts-cta` buttons,
//    Quarto italic accents, pink eyebrows to match the landing-page
//    treatment.
//  - variant="app":    in-app surface — uses shadcn `<Button>` and the
//    neutral/purple-37 palette per the M5 handoff ("never .ts-cta in-app").
//
// Score computation + flagged-rows download live in here; the wrapping
// page only composes the outer chrome.

import Papa from "papaparse";
import { readSheet } from "read-excel-file/browser";
import { useCallback, useMemo, useRef, useState } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { downloadXlsx } from "@/lib/xlsx";
import { csvRowToUtm, parsePasted } from "@shared/utm/parse";
import { flaggedToCsv, scoreRows } from "@shared/utm/score";
import type { UtmRow, UtmScoreResult } from "@shared/utm/types";

const SAMPLE = `https://skedsocial.com/?utm_source=instagram&utm_medium=social&utm_campaign=spring_launch
https://skedsocial.com/?utm_source=Instagram&utm_medium=organic-social&utm_campaign=spring_launch&utm_content=reel_01
https://skedsocial.com/?utm_source=ig&utm_medium=socialmedia&utm_campaign=sp
https://skedsocial.com/?utm_source=linkedin.com&utm_medium=paid-social&utm_campaign=2024q4_webinar
https://skedsocial.com/?utm_source=TikTok&utm_medium=organic-social
`;

export type UtmBulkCheckerVariant = "public" | "app";

interface UtmBulkCheckerProps {
  variant?: UtmBulkCheckerVariant;
  /** Optional notification when a fresh score is computed. */
  onResult?: (result: UtmScoreResult | null) => void;
}

export function UtmBulkChecker({ variant = "app", onResult }: UtmBulkCheckerProps) {
  const [pasted, setPasted] = useState("");
  const [result, setResult] = useState<UtmScoreResult | null>(null);
  const [csvRows, setCsvRows] = useState<UtmRow[]>([]);
  const [csvError, setCsvError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const combinedRows: UtmRow[] = useMemo(
    () => [...csvRows, ...parsePasted(pasted)],
    [pasted, csvRows],
  );

  const run = useCallback(() => {
    if (combinedRows.length === 0) {
      setResult(null);
      onResult?.(null);
      return;
    }
    const scored = scoreRows(combinedRows);
    setResult(scored);
    onResult?.(scored);
  }, [combinedRows, onResult]);

  function handleCsv(file: File) {
    setCsvError(null);
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (out) => {
        const headers = out.meta.fields ?? [];
        const rows: UtmRow[] = [];
        for (const record of out.data) {
          const row = csvRowToUtm(record, headers);
          if (row) rows.push(row);
        }
        if (rows.length === 0) {
          setCsvError(
            "We couldn't find UTM columns or a URL column in that CSV. Expected headers like utm_source, utm_medium, utm_campaign, or a 'url' column.",
          );
          setCsvRows([]);
        } else {
          setCsvRows(rows);
        }
      },
      error: (err) => setCsvError(err.message),
    });
  }

  async function handleXlsx(file: File) {
    setCsvError(null);
    try {
      const sheet = await readSheet(file);
      if (sheet.length < 2) {
        setCsvError(
          "That spreadsheet looks empty — we expected a header row and at least one URL.",
        );
        setCsvRows([]);
        return;
      }
      const [headerRow, ...dataRows] = sheet;
      const headers = headerRow.map((v) =>
        String(v ?? "")
          .trim()
          .toLowerCase(),
      );
      const rows: UtmRow[] = [];
      for (const r of dataRows) {
        const record: Record<string, string> = {};
        headers.forEach((h, i) => {
          const cell = r[i];
          record[h] = cell === null || cell === undefined ? "" : String(cell);
        });
        const row = csvRowToUtm(record, headers);
        if (row) rows.push(row);
      }
      if (rows.length === 0) {
        setCsvError(
          "We couldn't find UTM columns or a URL column in that sheet. Expected headers like utm_source, utm_medium, utm_campaign, or a 'url' column.",
        );
        setCsvRows([]);
      } else {
        setCsvRows(rows);
      }
    } catch (err) {
      setCsvError((err as Error).message);
      setCsvRows([]);
    }
  }

  function handleFile(file: File) {
    const name = file.name.toLowerCase();
    if (name.endsWith(".xlsx") || name.endsWith(".xlsm") || name.endsWith(".xlsb")) {
      void handleXlsx(file);
    } else {
      handleCsv(file);
    }
  }

  const filenameBase = `utm-flagged-${new Date().toISOString().slice(0, 10)}`;

  function downloadFlaggedCsv() {
    if (!result || result.flagged.length === 0) return;
    const blob = new Blob([flaggedToCsv(result.flagged)], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filenameBase}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async function downloadFlaggedXlsx() {
    if (!result || result.flagged.length === 0) return;
    await downloadXlsx({
      fileName: `${filenameBase}.xlsx`,
      sheets: [
        {
          sheetName: "Flagged rows",
          columns: [
            "Source",
            "Medium",
            "Campaign",
            "Content",
            "Term",
            "Source platform",
            "URL",
            "Sessions",
            "Issues",
          ],
          rows: result.flagged.map((r) => [
            r.source ?? "",
            r.medium ?? "",
            r.campaign ?? "",
            r.content ?? "",
            r.term ?? "",
            r.sourcePlatform ?? "",
            r.url ?? "",
            r.sessions ?? 0,
            r.issues.join(", "),
          ]),
        },
      ],
    });
  }

  function loadSample() {
    setPasted(SAMPLE);
    setCsvRows([]);
    const scored = scoreRows(parsePasted(SAMPLE));
    setResult(scored);
    onResult?.(scored);
  }

  const textareaClass =
    variant === "public"
      ? "mt-2 w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm text-ink shadow-xs transition-colors focus-visible:outline-none focus-visible:border-purple-500 focus-visible:shadow-[0_0_0_4px_var(--accent-blurple-soft)]"
      : "mt-2 w-full rounded-lg bg-white px-3 py-2 text-sm text-neutral-900 ring-1 ring-neutral-950/10 focus:outline-none focus:ring-2 focus:ring-purple-500";

  return (
    <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
      <div>
        <label
          htmlFor="utm-paste"
          className="flex items-center justify-between text-sm font-semibold text-neutral-800"
        >
          <span>Paste URLs (one per line)</span>
          <button
            type="button"
            onClick={loadSample}
            className="rounded-sm text-xs font-semibold text-purple-600 underline-offset-4 hover:text-purple-800 hover:underline focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2"
          >
            Load a sample
          </button>
        </label>
        <textarea
          id="utm-paste"
          name="utm-paste"
          value={pasted}
          onChange={(e) => setPasted(e.target.value)}
          rows={10}
          placeholder="https://example.com/?utm_source=instagram&utm_medium=organic-social&utm_campaign=..."
          className={textareaClass}
        />

        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3">
          {variant === "public" ? (
            <button
              type="button"
              onClick={run}
              disabled={combinedRows.length === 0}
              className="ts-cta disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
            >
              Score my UTMs <span className="ts-cta-arrow">→</span>
            </button>
          ) : (
            <Button type="button" onClick={run} disabled={combinedRows.length === 0}>
              Score my UTMs
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Button>
          )}
          <label className="inline-flex cursor-pointer items-center gap-1.5 text-sm font-medium text-neutral-700 hover:text-ink">
            <i
              className="fa-regular fa-file-arrow-up text-sm text-neutral-500"
              aria-hidden="true"
            />
            Upload CSV or Excel instead
            <input
              ref={fileRef}
              type="file"
              name="utm-csv"
              accept=".csv,text/csv,.xlsx,.xlsm,.xlsb,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleFile(f);
              }}
            />
          </label>
        </div>

        {csvError ? (
          <Alert variant="destructive" className="mt-5">
            <AlertTitle>File parse error</AlertTitle>
            <AlertDescription>{csvError}</AlertDescription>
          </Alert>
        ) : null}

        {csvRows.length > 0 ? (
          <p className="mt-4 text-xs text-neutral-500">
            File loaded: {csvRows.length} rows. They'll be scored together with any pasted URLs.
          </p>
        ) : null}

        <Alert className="mt-8">
          <i className="fa-regular fa-circle-info text-sm" aria-hidden="true" />
          <AlertTitle>What this audits</AlertTitle>
          <AlertDescription>
            Required params (source, medium, campaign). Canonical naming. Medium values. Platform
            metadata. Content granularity. Dark-social leakage.
          </AlertDescription>
        </Alert>
      </div>

      <UtmResultCard
        result={result}
        variant={variant}
        onDownloadCsv={downloadFlaggedCsv}
        onDownloadXlsx={downloadFlaggedXlsx}
      />
    </div>
  );
}

interface UtmResultCardProps {
  result: UtmScoreResult | null;
  variant?: UtmBulkCheckerVariant;
  onDownloadCsv: () => void;
  onDownloadXlsx: () => void | Promise<void>;
}

export function UtmResultCard({
  result,
  variant = "app",
  onDownloadCsv,
  onDownloadXlsx,
}: UtmResultCardProps) {
  const cardClass =
    variant === "public"
      ? "rounded-2xl border border-neutral-950/5 bg-white p-7 shadow-card"
      : "rounded-2xl bg-white p-7 ring-1 ring-neutral-950/5";

  if (!result) {
    return (
      <aside className={cardClass}>
        <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-neutral-500">
          Your score
        </p>
        <p className="mt-2 font-display text-[72px] font-[900] leading-none tracking-[-0.03em] text-neutral-400 tabular-nums">
          —
        </p>
        <p className="mt-4 max-w-[40ch] text-sm leading-[1.5] text-neutral-600 text-pretty">
          Paste URLs on the left or upload a CSV, then hit <strong>Score my UTMs</strong>.
        </p>
      </aside>
    );
  }

  const bandCopy: Record<UtmScoreResult["band"], string> = {
    excellent: "Excellent",
    good: "Good",
    "needs-work": "Needs work",
    broken: "Broken",
  };
  const bandColor: Record<UtmScoreResult["band"], string> = {
    excellent: "bg-green-500",
    good: "bg-green-400",
    "needs-work": "bg-yellow-500",
    broken: "bg-red-500",
  };
  const bandChipBg: Record<UtmScoreResult["band"], string> = {
    excellent: "bg-green-50 text-green-700",
    good: "bg-green-50 text-green-700",
    "needs-work": "bg-yellow-50 text-yellow-700",
    broken: "bg-red-50 text-red-700",
  };

  return (
    <aside className={cardClass}>
      <div className="mb-3 flex items-baseline justify-between gap-4">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.08em] text-neutral-500">
            {result.totals.rows} rows · {result.totals.sessions.toLocaleString()} sessions
          </p>
          <span
            className={cn(
              "mt-2 inline-flex rounded-full px-2.5 py-1.5 text-[13px] font-bold",
              bandChipBg[result.band],
            )}
          >
            {bandCopy[result.band]}
          </span>
        </div>
        <p className="font-display text-[72px] font-[900] leading-none tracking-[-0.03em] text-ink tabular-nums">
          {result.score}
        </p>
      </div>
      <div aria-hidden="true" className="mt-5 h-2 overflow-hidden rounded-full bg-neutral-100">
        <div
          className={`h-full rounded-full ${bandColor[result.band]}`}
          style={{ width: `${result.score}%` }}
        />
      </div>

      {result.issues.length === 0 ? (
        <p className="mt-6 text-sm text-neutral-600">
          No issues detected. Your taxonomy is in good shape.
        </p>
      ) : (
        <>
          <ul role="list" className="mt-6 divide-y divide-neutral-100">
            {result.issues.slice(0, 8).map((issue) => (
              <li
                key={`${issue.category}-${issue.label}`}
                className="flex items-start justify-between gap-4 py-3 text-sm"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      aria-hidden="true"
                      className={cn(
                        "size-2 shrink-0 rounded-full",
                        issue.severity === "severe"
                          ? "bg-red-500"
                          : issue.severity === "medium"
                            ? "bg-yellow-500"
                            : "bg-neutral-300",
                      )}
                    />
                    <span className="truncate text-ink">{issue.label}</span>
                  </div>
                  {issue.examples.length > 0 ? (
                    <p className="mt-1 ml-3.5 truncate text-xs text-neutral-500">
                      e.g. {issue.examples.slice(0, 2).join(" · ")}
                    </p>
                  ) : null}
                </div>
                <span className="shrink-0 text-xs font-semibold text-neutral-500 tabular-nums">
                  {issue.rowCount} row{issue.rowCount === 1 ? "" : "s"}
                </span>
              </li>
            ))}
          </ul>
          {result.flagged.length > 0 ? (
            <div className="mt-6 flex flex-wrap items-center gap-2">
              <span className="text-sm text-neutral-600">
                Download {result.flagged.length} flagged row
                {result.flagged.length === 1 ? "" : "s"}:
              </span>
              {variant === "public" ? (
                <>
                  <button
                    type="button"
                    onClick={onDownloadXlsx}
                    className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-ink shadow-xs transition-all duration-200 hover:-translate-y-0.5 hover:border-purple-500/30 hover:shadow-sm focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2"
                  >
                    <i className="fa-regular fa-file-excel text-xs" aria-hidden="true" />
                    Excel
                  </button>
                  <button
                    type="button"
                    onClick={onDownloadCsv}
                    className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 shadow-xs transition-all duration-200 hover:-translate-y-0.5 hover:border-neutral-400 hover:shadow-sm focus-visible:outline-2 focus-visible:outline-purple-500 focus-visible:outline-offset-2"
                  >
                    <i className="fa-regular fa-file-csv text-xs" aria-hidden="true" />
                    CSV
                  </button>
                </>
              ) : (
                <>
                  <Button
                    type="button"
                    onClick={onDownloadXlsx}
                    variant="ghost"
                    size="sm"
                    className="text-purple-700"
                  >
                    <i className="fa-regular fa-file-excel text-sm" aria-hidden="true" />
                    Excel
                  </Button>
                  <Button
                    type="button"
                    onClick={onDownloadCsv}
                    variant="ghost"
                    size="sm"
                    className="text-neutral-600"
                  >
                    <i className="fa-regular fa-file-csv text-sm" aria-hidden="true" />
                    CSV
                  </Button>
                </>
              )}
            </div>
          ) : null}
        </>
      )}
    </aside>
  );
}
